import React from 'react';
import { motion } from 'framer-motion';
import { Plus, List, Grid3x3, Filter } from 'lucide-react';
import clsx from 'clsx';
import { FilteredRolodexView } from '../../../FilteredRolodexView';
import RegisterBusinessForm from '../../../RegisterBusinessForm';
import { Button } from '../../ui/Button';

const VIEW_MODES = [
    { mode: 'rolodex', icon: <List size={14} />,    title: 'תצוגת גלילה אופקית' },
    { mode: 'compact', icon: <Grid3x3 size={14} />, title: 'תצוגת רשת קומפקטית' },
];

/**
 * The Businesses, Events and Barters tabs all follow the same shape:
 *   info box → add button → view-mode picker → filter button → entity list.
 *
 * This component captures that pattern. Pass entity-specific labels and
 * data via props.
 *
 *   <EntityListTab
 *     entityType="event"
 *     infoIcon={CalendarDays}
 *     infoTitle="מפגשים ואירועים"
 *     infoDescription="..."
 *     addLabel="הוספת מפגש חדש"
 *     items={events}
 *     categories={eventsCategories}
 *     ...
 *   />
 */
export function EntityListTab({
    // Section header (was: info box)
    infoIcon: Icon,
    infoTitle,
    infoDescription,
    // (legacy props kept for call-site compatibility; ignored visually)
    infoCollapsed,        // eslint-disable-line no-unused-vars
    onToggleInfo,         // eslint-disable-line no-unused-vars
    // Add button
    addLabel,
    isLoggedIn,
    showCreateModal,
    onOpenCreate,
    onCloseCreate,
    entityType,         // 'business' | 'event' | 'barter' — passed to RegisterBusinessForm
    rolodexType,        // 'business' | 'event' | 'barter' — passed to FilteredRolodexView (sometimes omitted for businesses)
    // Filters & view
    viewMode,
    onViewModeChange,
    showFilters,
    onToggleFilters,
    activeFilterCount,
    onFilterChange,
    // Data
    items,
    categories,
    zone,
    zoneCenter,
    chatId,
    favoritesLists,
    onToggleFavorite,
    onCreateFavoriteList,
}) {
    const count = Array.isArray(items) ? items.length : 0;
    return (
        <div className="space-y-6">
            {/* Section header — quiet, identifies the section without occupying a card */}
            <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="flex items-end justify-between gap-4 flex-wrap"
            >
                <div className="flex items-start gap-3 min-w-0">
                    {Icon && (
                        <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-brand-50 text-brand-700 ring-1 ring-brand-100">
                            <Icon className="w-5 h-5" strokeWidth={2.25} />
                        </span>
                    )}
                    <div className="min-w-0">
                        <h2 className="heading-lg text-ink leading-tight">
                            {infoTitle}
                            {count > 0 && (
                                <span className="ms-2 inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-pill bg-surface-muted text-ink-muted text-sm font-semibold align-middle">
                                    {count}
                                </span>
                            )}
                        </h2>
                        {infoDescription && (
                            <p className="text-sm text-ink-muted mt-1 max-w-xl">{infoDescription}</p>
                        )}
                    </div>
                </div>
                {isLoggedIn && (
                    <Button
                        variant="primary"
                        size="sm"
                        icon={<Plus />}
                        onClick={onOpenCreate}
                    >
                        {addLabel}
                    </Button>
                )}
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="flex items-center justify-between gap-3"
            >
                {/* View mode picker — minimal icon row, active state feels "pressed in" */}
                <div role="tablist" aria-label="מצב תצוגה" className="inline-flex items-center gap-1">
                    {VIEW_MODES.map(({ mode, icon, title }) => {
                        const active = viewMode === mode;
                        return (
                            <button
                                key={mode}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                onClick={() => onViewModeChange(mode)}
                                title={title}
                                className={clsx(
                                    'group relative inline-flex items-center justify-center w-10 h-10 rounded-xl',
                                    'transition-[background-color,color,box-shadow] duration-200 ease-smooth',
                                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                                    active
                                        ? 'bg-gradient-to-br from-brand-50 to-brand-100/70 text-brand-700 shadow-inset-soft ring-1 ring-brand-200/60'
                                        : 'text-ink-faint hover:text-brand-700 hover:bg-surface-muted/70',
                                )}
                            >
                                {React.cloneElement(icon, { size: 16, strokeWidth: active ? 2.5 : 2.25 })}
                                {/* Bottom indicator dot when active */}
                                {active && (
                                    <span
                                        aria-hidden="true"
                                        className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-pill bg-brand-500"
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Filter button — pill with badge */}
                {categories.length > 0 && (
                    <button
                        type="button"
                        onClick={onToggleFilters}
                        aria-pressed={showFilters || activeFilterCount > 0}
                        className={clsx(
                            'relative inline-flex items-center gap-2 px-4 h-9 rounded-pill text-xs font-semibold',
                            'transition-[background-color,color,box-shadow,transform] duration-200 ease-smooth',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                            'active:scale-[0.98]',
                            (activeFilterCount > 0 || showFilters)
                                ? 'bg-brand-500 text-ink-inverse shadow-sm hover:bg-brand-600'
                                : 'bg-surface text-ink-muted ring-1 ring-line-subtle hover:text-brand-700 hover:bg-surface-muted',
                        )}
                    >
                        <Filter size={14} strokeWidth={2.25} />
                        <span>סינון</span>
                        {activeFilterCount > 0 && (
                            <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-pill bg-surface text-brand-700 text-2xs font-bold shadow-sm">
                                {activeFilterCount}
                            </span>
                        )}
                    </button>
                )}
            </motion.div>

            {showCreateModal && (
                <RegisterBusinessForm
                    chatId={chatId}
                    initialType={entityType}
                    initialZone={zone}
                    onClose={onCloseCreate}
                />
            )}

            <FilteredRolodexView
                items={items}
                chatId={chatId}
                categories={categories}
                {...(rolodexType && { type: rolodexType })}
                fromView="zone-details"
                viewMode={viewMode}
                onViewModeChange={onViewModeChange}
                showFilters={showFilters}
                onToggleFilters={onToggleFilters}
                onFilterChange={onFilterChange}
                zoneCenter={zoneCenter}
                favoritesLists={favoritesLists}
                onToggleFavorite={onToggleFavorite}
                onCreateFavoriteList={onCreateFavoriteList}
                zone={zone}
            />
        </div>
    );
}

export default EntityListTab;
